import { valibotResolver } from '@hookform/resolvers/valibot'
import { Input } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { type FormEvent, useCallback, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaCheckCircle } from 'react-icons/fa'

import {
  BasicTable,
  BasicTbody,
  BasicTd,
  BasicTh,
  BasicTr,
  Button,
  ModalBody,
  ModalFooter,
  RequiredTag
} from '@/components/elements'
import {
  ACTIONS,
  ERROR_MESSAGES,
  LABELS,
  SUCCESS_MESSAGES
} from '@/constants/common/message.constants'
import { toastApiErrorMessage } from '@/features/shared/utils'
import { type TruckModelEntity } from '@/libs/api'

import { createTruckModel } from '../../api'
import {
  createDefaultTruckModelFormValue,
  createTruckModelFormValueSchema,
  type CreateTruckModelFormValue
} from '../../models'

type Props = {
  onClose: () => void
  onSuccess: (site: TruckModelEntity) => Promise<void>
  tenantKey: string
  dcId: number | undefined
}

export const TruckModelCreateModalContent: FC<Props> = ({
  onClose,
  onSuccess,
  tenantKey,
  dcId
}) => {
  const formMethods = useForm<CreateTruckModelFormValue>({
    defaultValues: createDefaultTruckModelFormValue,
    resolver: valibotResolver(createTruckModelFormValueSchema)
  })

  const {
    control,
    formState: { isSubmitting, isValid },
    handleSubmit,
    reset
  } = formMethods

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      // formが入れ子になっている場合の対応
      // - submitイベントの親要素への伝播を止める
      if (event) {
        if (typeof event.preventDefault === 'function') {
          event.preventDefault()
        }
        if (typeof event.stopPropagation === 'function') {
          event.stopPropagation()
        }
      }
      return handleSubmit(async (formValue) => {
        try {
          const { data } = await createTruckModel(tenantKey, {
            ...formValue,
            dcId
          })
          onSuccess(data)
        } catch (error) {
          toastApiErrorMessage(error, ERROR_MESSAGES.FAILED_TO_REGISTER)
          return
        }
        toast.success(SUCCESS_MESSAGES.SUCCEEDED_TO_REGISTER)
        reset()
        onClose()
      })(event)
    },
    [handleSubmit, onClose, onSuccess, reset, tenantKey, dcId]
  )

  return (
    <form onSubmit={onSubmit}>
      <ModalBody>
        <BasicTable className="w-full table-fixed">
          <colgroup>
            <col className="w-[160px]" />
            <col />
          </colgroup>
          <BasicTbody>
            <BasicTr>
              <BasicTh>
                <label htmlFor="code">{LABELS.TRUCK_MODEL_CODE}</label>
                <RequiredTag className="ml-2" />
              </BasicTh>
              <BasicTd>
                <Controller
                  control={control}
                  name={'code'}
                  render={({ field, fieldState: { error } }) => (
                    <FormItem
                      htmlFor={field.name}
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input {...field} id={field.name} />
                    </FormItem>
                  )}
                />
              </BasicTd>
            </BasicTr>

            <BasicTr>
              <BasicTh>
                <label htmlFor="name">{LABELS.TRUCK_MODEL_NAME}</label>
                <RequiredTag className="ml-2" />
              </BasicTh>
              <BasicTd>
                <Controller
                  control={control}
                  name={'name'}
                  render={({ field, fieldState: { error } }) => (
                    <FormItem
                      htmlFor={field.name}
                      validateStatus={error?.message ? 'error' : undefined}
                      help={error?.message}
                      className="m-0"
                    >
                      <Input {...field} id={field.name} />
                    </FormItem>
                  )}
                />
              </BasicTd>
            </BasicTr>
          </BasicTbody>
        </BasicTable>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose}>{ACTIONS.CANCEL}</Button>
        <Button
          type="primary"
          icon={<FaCheckCircle />}
          htmlType="submit"
          disabled={!isValid}
          loading={isSubmitting}
        >
          {ACTIONS.REGISTER}
        </Button>
      </ModalFooter>
    </form>
  )
}
